import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { useEventLogging } from '../../../../models/Logging';
import { DefaultSupportedIndustries } from '../../../../models';
import { useErrorMessages, useFullscreenModal, useUserSession } from '../../../../models/hooks/appStateHooks';
import { ISystemAutomationTemplate } from '../../../../models/AdminModels';
import { invalidateAllIndustryAutomationTemplatesQueries } from '../../../../queries/Admin';
import { AdminUserSessionContext } from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { CreateAdminAutomationTemplatePage } from '../../../../web/containers/CreateAutomationTemplate';
import { INavigationItemProps } from '../../../../web/containers/MainContainer';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any>, INavigationItemProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

export const CreateIndustryAutomationTemplate: React.FC<IProps> = props => {
	const { className, styles = [] } = props;
	const fullscreenModal = useFullscreenModal();
	const { logEvent } = useEventLogging('CreateIndustryAutomationTemplate');
	const userSession = useUserSession<AdminUserSessionContext>();
	const errorMessages = useErrorMessages();
	const [loading, setLoading] = React.useState<boolean>(false);

	const onRequestEdit = React.useCallback(
		(templateId: string) => {
			logEvent('EditTemplate', { id: templateId });

			// The create flow creates a plain automation template. For the admin automation library we also need to
			// register it as a system/industry template so the editor can load industries + publish/archive state.
			setLoading(true);

			const createSystemTemplatePromise = userSession?.webServiceHelper.callAsync<ISystemAutomationTemplate>(
				`admin/AutomationTemplate/${templateId}/Industry`,
				'PUT',
				DefaultSupportedIndustries
			);

			if (!createSystemTemplatePromise) {
				setLoading(false);
				fullscreenModal?.history.push({ pathname: `/automations/${templateId}` });
				return true;
			}

			createSystemTemplatePromise
				.then(systemTemplate => {
					invalidateAllIndustryAutomationTemplatesQueries();
					fullscreenModal?.history.push({
						pathname: `/automations/${templateId}`,
						state: { viewModel: systemTemplate },
					});
				})
				.catch(error => {
					errorMessages?.pushApiError(error);
					// Fall back to a state-less navigation; EditIndustryAutomationTemplate can fetch by ID.
					fullscreenModal?.history.push({ pathname: `/automations/${templateId}` });
				})
				.finally(() => {
					setLoading(false);
				});

			return true;
		},
		[errorMessages, fullscreenModal, logEvent, userSession]
	);

	return (
		<div className={`${css(styleSheet.container, ...styles)} create-industry-automation-template ${className || ''}`}>
			<CreateAdminAutomationTemplatePage onRequestEdit={onRequestEdit} />
			{!!loading && <LoadingSpinner className={css(baseStyleSheet.absoluteCenter)} type='large' />}
		</div>
	);
};
